import AppLayout from "../layouts/AppLayout";

import '@mantine/dates/styles.css';

import { Scheduler } from "../components/schedule/Scheduler";
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { CalendarBooking, CalendarEvent, getAvailabilityCalendarUuids, PublicSchedule } from '../utils/schedule';
import { ScheduleConfirmModal, ScheduleUpdateModal } from '../components/schedule/ScheduleConfirmModal';
import { Head } from 'vite-react-ssg';
import API from '../utils/const';
import { handleApiError } from '../utils/app';
import { useTranslation } from 'react-i18next';
import { ScheduleConfirmed } from '../components/schedule/ScheduleConfirmed';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { createToast } from '../components/Toast';
import { MessageKind } from '../utils/common';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

export function InnerSchedulerPage() {
    const { t } = useTranslation();
    const { setRedirectOnUnauthenticated } = useAuth();
    const [openedConfirmModal, { open: openConfirmModal, close: closeConfirmModal }] = useDisclosure();
    const [openedUpdateModal, { open: openUpdateModal, close: closeUpdateModal }] = useDisclosure();
    const [selectedDateTime, setSelectedIsoDateTime] = useState<Date | null>(null);
    const [schedule, setSchedule] = useState<PublicSchedule | null>(null);
    const [existingEvents, setExistingEvents] = useState<CalendarEvent[] | null>(null);
    const [confirmedBookingId, setConfirmedBookingId] = useState<string | null>(null);
    const [currentBooking, setCurrentBooking] = useState<CalendarEvent | null>(null);
    const [bookingDoesntExist, setBookingDoesntExist] = useState(false);


    const currentBookingId = typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search).get('booking_uuid') : null;

    const onConfirm = (isoDateTime: Date) => {
        setSelectedIsoDateTime(isoDateTime);
        openConfirmModal();
    }

    const onUpdate = (isoDateTime: Date) => {
        setSelectedIsoDateTime(isoDateTime);
        openUpdateModal();
    }

    if (typeof window === 'undefined') {
        return (
            <AppLayout include_navbar={false} onlyRequired={true}>
                <Head>
                    <title>Schedule Meeting</title>
                    <meta name="description" content="Calensync is the best way to schedule your meetings connected to your Google, Outlook, Apple, or iCalendar calendars. Avoid double booking and make sure you have the right time for your meetings." />

                </Head>
            </AppLayout>
        )
    }

    const schedule_uuid = new URLSearchParams(window.location.search).get('schedule_uuid');
    const encoded_preview = new URLSearchParams(window.location.search).get('preview');

    const parseBusySlots = (body: { start: string; end: string }[]): CalendarEvent[] =>
        body.map((event) => ({
            start: dayjs(event.start),
            end: dayjs(event.end),
        }));

    const fetchPreviewBusySlots = async (schedule: PublicSchedule) => {
        const calendarIds = getAvailabilityCalendarUuids(schedule);
        const responses = await Promise.all(
            calendarIds.map((calendarId) => fetch(`${API}/calendar/${calendarId}/busy`)),
        );
        for (const response of responses) {
            if (!response.ok) {
                handleApiError(response, t);
                return;
            }
        }
        const bodies = await Promise.all(responses.map((response) => response.json()));
        setExistingEvents(bodies.flatMap(parseBusySlots));
    }

    const fetchPublicSchedule = async (schedule_id: string) => {
        const response = await fetch(`${API}/schedule/${schedule_id}/public`);
        if (!response.ok) {
            handleApiError(response, t, true);
        }
        setSchedule(await response.json());
    }

    const fetchExistingEvents = async (schedule: PublicSchedule) => {
        const response = await fetch(`${API}/schedule/${schedule.uuid}/busy`);
        if (!response.ok) {
            handleApiError(response, t);
            return;
        }
        const body = await response.json();
        setExistingEvents(parseBusySlots(body));
    }

    const fetchScheduleAndBookingForBookingId = async (booking_id: string) => {
        const response = await fetch(`${API}/booking/${booking_id}`);
        if (!response.ok) {
            if (response.status == 404) {
                setBookingDoesntExist(true);
            } else {
                handleApiError(response, t, true);
            }
            return;
        }
        const json = await response.json();
        json.booking.start = dayjs(json.booking.start);
        json.booking.end = dayjs(json.booking.end);
        setCurrentBooking(json.booking);
        fetchPublicSchedule(json.schedule_uuid);
    }

    useEffect(() => {
        setRedirectOnUnauthenticated(false);
    }, [setRedirectOnUnauthenticated]);

    useEffect(() => {
        if (schedule_uuid) {
            fetchPublicSchedule(schedule_uuid);
        }
        else if (encoded_preview) {
            const preview = atob(decodeURIComponent(encoded_preview));
            const json = JSON.parse(preview);
            setSchedule(json);
        } else if (currentBookingId) {
            fetchScheduleAndBookingForBookingId(currentBookingId);
        }
    }, []);

    useEffect(() => {
        if (schedule) {
            if (encoded_preview) {
                fetchPreviewBusySlots(schedule);
            } else {
                fetchExistingEvents(schedule);
            }
        }
    }, [schedule]);

    const onPresubmit = encoded_preview ? () => {
        createToast("This is a preview of the schedule. You can't book an appointment.", MessageKind.Info);
    } : null;

    return <>
        <ScheduleConfirmModal dateTime={selectedDateTime} schedule={schedule} opened={openedConfirmModal} onClose={closeConfirmModal} onConfirm={(bookingId: string) => setConfirmedBookingId(bookingId)} onPresubmit={onPresubmit} />
        <ScheduleUpdateModal dateTime={selectedDateTime} schedule={schedule} opened={openedUpdateModal} onClose={closeUpdateModal} onConfirm={(bookingId: string) => setConfirmedBookingId(bookingId)} currentBookingUuid={currentBookingId} />
        {!confirmedBookingId && !bookingDoesntExist && (
            existingEvents == null
                ? <Stack><Center h={'100vh'}><Loader /></Center></Stack>
                : <Scheduler schedule={schedule} existingEvents={existingEvents} onConfirm={onConfirm} onUpdate={onUpdate} currentBooking={currentBooking} />
        )}
        {confirmedBookingId && (
            <ScheduleConfirmed bookingId={confirmedBookingId} />
        )}
        {bookingDoesntExist && (
            <Stack justify="center" align="center" h={'100vh'}>
                <Text>404: Booking not found</Text>
                <Button color='primary.8' onClick={() => window.location.href = '/'}>Home</Button>
            </Stack>
        )}
    </>;
}

export default function SchedulerPage() {
    return <AppLayout include_navbar={false} onlyRequired={true}>
        <Head>
            <title>Schedule Meeting</title>
            <meta name="description" content="Calensync is the best way to schedule your meetings connected to your Google, Outlook, Apple, or iCalendar calendars. Avoid double booking and make sure you have the right time for your meetings." />
        </Head>
        <InnerSchedulerPage />
    </AppLayout>
}
